import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { Button } from '@rentspree/component-2023.components.atoms.button';
import { TextInput } from '@rentspree/component-2023.components.atoms.text-input';

import {
  OwnerInfo,
  UserRole,
  shouldShowOwnerInfoStep,
} from '../../types/tenantScreening';
import { MultiStepLayout } from '../templates/MultiStepLayout';

import { SectionHeader } from './SectionHeader';

export interface OwnerInfoStepProps {
  ownerInfo: Partial<OwnerInfo>;
  userRole?: UserRole;
  onOwnerInfoChange: (info: Partial<OwnerInfo>) => void;
  onNext: () => void;
  onBack: () => void;
  onHomeClick?: () => void;
  onExitClick?: () => void;
  isLoading?: boolean;
  // MultiStepLayout props - optional to allow content-only usage
  currentStep?: number;
  total?: number;
  currentSubStepPercentage?: number;
  title?: string;
  description?: string;
  // If true, renders only content without MultiStepLayout wrapper
  contentOnly?: boolean;
  // If true, shows "Add later" button
  showAddLaterButton?: boolean;
  // Hide the internal SectionHeader when using MultiStepLayout's title/description
  hideInternalHeader?: boolean;
}

export interface OwnerInfoContentProps {
  ownerInfo: Partial<OwnerInfo>;
  onOwnerInfoChange: (info: Partial<OwnerInfo>) => void;
  isLoading?: boolean;
  title?: string;
  description?: string;
  showAddLaterButton?: boolean;
  onAddLater?: (() => void) | undefined;
  // Hide the internal SectionHeader when using MultiStepLayout's title/description
  hideInternalHeader?: boolean;
}

interface FormValidation {
  firstName: boolean;
  lastName: boolean;
  email: boolean;
  phone: boolean;
}

// Content-only component for reuse
export const OwnerInfoContent: React.FC<OwnerInfoContentProps> = ({
  ownerInfo,
  onOwnerInfoChange,
  isLoading = false,
  title = 'Tell us about the property owner',
  description = "We'll use this information to automate this transaction. This information will be kept secure between you and the owner.",
  showAddLaterButton = false,
  onAddLater,
  hideInternalHeader = false,
}) => {
  const [localOwnerInfo, setLocalOwnerInfo] =
    useState<Partial<OwnerInfo>>(ownerInfo);
  const [validation, setValidation] = useState<FormValidation>({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  });

  // Sync local state with external ownerInfo prop
  useEffect(() => {
    setLocalOwnerInfo(ownerInfo);
  }, [ownerInfo]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    return phoneRegex.test(phone);
  };

  const handleFieldChange = (field: keyof OwnerInfo, value: string) => {
    const updatedInfo = { ...localOwnerInfo, [field]: value };
    setLocalOwnerInfo(updatedInfo);
    onOwnerInfoChange(updatedInfo);

    // Update validation
    const newValidation = { ...validation };
    switch (field) {
      case 'firstName':
        newValidation.firstName = value.trim().length > 0;
        break;
      case 'lastName':
        newValidation.lastName = value.trim().length > 0;
        break;
      case 'email':
        newValidation.email = validateEmail(value);
        break;
      case 'phone':
        newValidation.phone = validatePhone(value);
        break;
    }
    setValidation(newValidation);
  };

  return (
    <Box
      sx={{
        maxWidth: { xs: '100%', sm: 600, md: 700 },
        mx: 'auto',
        px: { xs: 1, sm: 2, md: 3 },
        width: '100%',
      }}
    >
      {!hideInternalHeader && (
        <SectionHeader title={title} description={description} align='center' />
      )}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant='body1' sx={{ mb: 1, fontWeight: 500 }}>
            First Name *
          </Typography>
          <TextInput
            placeholder='Enter first name'
            value={localOwnerInfo.firstName || ''}
            onChange={e => handleFieldChange('firstName', e.target.value)}
            fullWidth
            disabled={isLoading}
            error={
              localOwnerInfo.firstName !== undefined && !validation.firstName
            }
            helperText={
              localOwnerInfo.firstName !== undefined && !validation.firstName
                ? 'First name is required'
                : ''
            }
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant='body1' sx={{ mb: 1, fontWeight: 500 }}>
            Last Name *
          </Typography>
          <TextInput
            placeholder='Enter last name'
            value={localOwnerInfo.lastName || ''}
            onChange={e => handleFieldChange('lastName', e.target.value)}
            fullWidth
            disabled={isLoading}
            error={
              localOwnerInfo.lastName !== undefined && !validation.lastName
            }
            helperText={
              localOwnerInfo.lastName !== undefined && !validation.lastName
                ? 'Last name is required'
                : ''
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant='body1' sx={{ mb: 1, fontWeight: 500 }}>
            Email Address *
          </Typography>
          <TextInput
            placeholder='Enter email address'
            type='email'
            value={localOwnerInfo.email || ''}
            onChange={e => handleFieldChange('email', e.target.value)}
            fullWidth
            disabled={isLoading}
            error={localOwnerInfo.email !== undefined && !validation.email}
            helperText={
              localOwnerInfo.email !== undefined && !validation.email
                ? 'Please enter a valid email address'
                : ''
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant='body1' sx={{ mb: 1, fontWeight: 500 }}>
            Phone Number *
          </Typography>
          <TextInput
            placeholder='(555) 123-4567'
            type='tel'
            value={localOwnerInfo.phone || ''}
            onChange={e => handleFieldChange('phone', e.target.value)}
            fullWidth
            disabled={isLoading}
            error={localOwnerInfo.phone !== undefined && !validation.phone}
            helperText={
              localOwnerInfo.phone !== undefined && !validation.phone
                ? 'Please enter a valid phone number'
                : ''
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant='body1' sx={{ mb: 1, fontWeight: 500 }}>
            Company (Optional)
          </Typography>
          <TextInput
            placeholder='Enter company name'
            value={localOwnerInfo.company || ''}
            onChange={e => handleFieldChange('company', e.target.value)}
            fullWidth
            disabled={isLoading}
          />
        </Grid>
      </Grid>

      {showAddLaterButton && onAddLater && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant='text'
            color='primary'
            onClick={onAddLater}
            disabled={isLoading}
            size='medium'
            sx={{ minWidth: 120 }}
          >
            Add later
          </Button>
        </Box>
      )}
    </Box>
  );
};

// Full step component with MultiStepLayout and skip logic
export const OwnerInfoStep: React.FC<OwnerInfoStepProps> = ({
  ownerInfo,
  userRole,
  onOwnerInfoChange,
  onNext,
  onBack,
  onHomeClick,
  onExitClick,
  isLoading = false,
  currentStep = 3,
  total = 7,
  currentSubStepPercentage = 100,
  title = 'Tell us about the property owner',
  description = "We'll use this information to automate this transaction. This information will be kept secure between you and the owner.",
  contentOnly = false,
  showAddLaterButton = true,
  hideInternalHeader = false,
}) => {
  const [localOwnerInfo, setLocalOwnerInfo] =
    useState<Partial<OwnerInfo>>(ownerInfo);

  // Skip this step if user is landlord (preserve original logic)
  useEffect(() => {
    if (userRole && !shouldShowOwnerInfoStep(userRole)) {
      onNext();
    }
  }, [userRole, onNext]);

  const handleOwnerInfoChange = (info: Partial<OwnerInfo>) => {
    setLocalOwnerInfo(info);
    onOwnerInfoChange(info);
  };

  const handleNext = () => {
    if (!isLoading) {
      onNext();
    }
  };

  const handleAddLater = () => {
    if (!isLoading) {
      onNext();
    }
  };

  // Don't render if this step should be skipped (preserve original logic)
  if (userRole && !shouldShowOwnerInfoStep(userRole)) {
    return null;
  }

  // If contentOnly, return just the content
  if (contentOnly) {
    return (
      <OwnerInfoContent
        ownerInfo={localOwnerInfo}
        onOwnerInfoChange={handleOwnerInfoChange}
        isLoading={isLoading}
        title={title}
        description={description}
        showAddLaterButton={showAddLaterButton}
        onAddLater={showAddLaterButton ? handleAddLater : undefined}
        hideInternalHeader={hideInternalHeader}
      />
    );
  }

  // Return full step with MultiStepLayout
  return (
    <MultiStepLayout
      currentStep={currentStep}
      total={total}
      currentSubStepPercentage={currentSubStepPercentage}
      onBack={onBack}
      onNext={handleNext}
      onHomeClick={onHomeClick}
      onExitClick={onExitClick}
      backDisabled={isLoading}
      nextDisabled={isLoading}
    >
      <OwnerInfoContent
        ownerInfo={localOwnerInfo}
        onOwnerInfoChange={handleOwnerInfoChange}
        isLoading={isLoading}
        title={title}
        description={description}
        showAddLaterButton={showAddLaterButton}
        onAddLater={showAddLaterButton ? handleAddLater : undefined}
        hideInternalHeader={hideInternalHeader}
      />
    </MultiStepLayout>
  );
};

export default OwnerInfoStep;
